import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MemoryIcon from '@mui/icons-material/Memory';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PartSummaryBarProps } from './PartSummaryBar.props';
import { PartSummaryBarActions } from './PartSummaryBarActions';
import { COLORS } from '../../../configs/colors';
import { StatusBadge } from '../../atoms/StatusBadge';

const Divider: React.FC = () => (
  <Box sx={{ width: '1px', height: 22, bgcolor: 'v4.border', flexShrink: 0 }} />
);

const TAG_COLORS = [
  { bg: COLORS.TagBlueBg, text: COLORS.TagBlueText },
  { bg: COLORS.TagGreenBg, text: COLORS.TagGreenText },
  { bg: COLORS.TagAmberBg, text: COLORS.TagAmberText },
  { bg: COLORS.TagPurpleBg, text: COLORS.TagPurpleText },
  { bg: COLORS.TagRedBg, text: COLORS.TagRedText },
  { bg: COLORS.TagIndigoBg, text: COLORS.TagIndigoText },
];

const labelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
};

export const PartSummaryBar: React.FC<PartSummaryBarProps> = ({
  serial1,
  partTypeId,
  partTypeName,
  partNumber,
  conditionName,
  locationHierarchy,
  tags = [],
  hasDuplicateSerial = false,
  saving = false,
  canEdit,
  isDirty = false,
  onSave,
  onDiscard,
  rightSlot,
}) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'v4.border',
        borderRadius: '8px',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {/* Row 1 — primary identity: icon, serials, type/number, go-to-type */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Part" placement="top" arrow>
            <MemoryIcon sx={{ fontSize: 22, color: 'v4.accent', flexShrink: 0 }} />
          </Tooltip>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              color: hasDuplicateSerial ? 'error.main' : 'text.primary',
            }}
            title={
              hasDuplicateSerial
                ? `${serial1} — another part shares this Serial 1 + Part Type`
                : serial1
            }
          >
            {serial1}
          </Typography>

          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '15px' }}>
            :
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
              title={partTypeName}
            >
              {partTypeName || '—'}
            </Typography>
            {partNumber && (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                · {partNumber}
              </Typography>
            )}
            {partTypeId ? (
              <Tooltip title="Go to part type" placement="top" arrow>
                <IconButton
                  component={RouterLink}
                  to={`/configure/partTypes/${partTypeId}`}
                  size="small"
                  aria-label="Go to part type"
                  sx={{
                    p: 0.25,
                    color: 'text.secondary',
                    '&:hover': { color: 'v4.accent' },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        {/* Row 2 — secondary metadata: condition, location hierarchy, tags */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {conditionName && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                '& > *': { fontSize: '11px', fontWeight: 700, px: 1, py: 0.25 },
              }}
            >
              <StatusBadge status={conditionName} size="small" />
            </Box>
          )}

          {locationHierarchy && (
            <>
              {conditionName && <Divider />}
              <Typography sx={labelSx}>Location</Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flexShrink: 1,
                }}
                title={locationHierarchy}
              >
                {locationHierarchy}
              </Typography>
            </>
          )}

          {tags.length > 0 && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', flexShrink: 1 }}>
                {tags.map((tag, idx) => {
                  const colors = TAG_COLORS[idx % TAG_COLORS.length];
                  return (
                    <Chip
                      key={`${tag.id ?? idx}-${tag.text ?? idx}`}
                      label={tag.text}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        fontWeight: 600,
                        bgcolor: colors.bg,
                        color: colors.text,
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, ml: 'auto', alignItems: 'center' }}>
        <PartSummaryBarActions
          saving={saving}
          canEdit={canEdit}
          isDirty={isDirty}
          onSave={onSave}
          onDiscard={onDiscard}
        />
        {rightSlot}
      </Box>
    </Box>
  );
};
